import { createAsyncThunk } from '@reduxjs/toolkit'
import { RootState } from '@store/redux.store'
import { kanbanApi } from '../api/kanban.api'
import { crmApi } from '@/api'
import type { CrmKanbanSourceItem } from '@/api/types/crm/crm.types'
import type { KanbanCard } from './kanban.types'
import {
  GetKanbanCardResponse,
  KanbanCardSyncDataDto,
  KanbanManager,
  KanbanMoveCardRequest,
  KanbanSourceOption,
  KanbanState,
} from './kanban.types'
import { parseRefetchKanbanColumnArg } from './kanbanRefetchColumn'
import type { RefetchKanbanColumnArg } from './kanbanRefetchColumn'
import {
  cardRemoved,
  patchKanbanCardFunnelListRow,
  prependKanbanCardFromSync,
  upsertKanbanManagerFromCard,
} from './kanban.slice'
import { isError } from '@/helpers/checkQuery'
import type { LeadField } from '@/api/types/crm/crm.types'
import {
  buildKanbanStatusLimitsFromColumns,
  KANBAN_DEFAULT_COLUMN_ITEMS_LIMIT,
} from '../lib/kanbanBoardLimits'
import {
  buildFunnelListRowFromKanbanSync,
  type KanbanFunnelListRowContext,
} from '../lib/buildKanbanFunnelListRow'

export { KANBAN_DEFAULT_COLUMN_ITEMS_LIMIT as DEFAULT_COLUMN_ITEMS_LIMIT } from '../lib/kanbanBoardLimits'

type AppState = RootState & {
  crmKanban: KanbanState
}

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message) return error.message
  const ax = error as {
    response?: { data?: { message?: unknown; error?: unknown } }
  }
  const d = ax?.response?.data
  if (d) {
    const raw = d.message ?? d.error
    if (Array.isArray(raw)) return raw.map(String).join(' ')
    if (typeof raw === 'string' && raw.trim()) return raw.trim()
  }
  return fallback
}

const mapManager = (item: any): KanbanManager => ({
  id: Number(item.id),
  fio: String(item.fio ?? item.name ?? `ID ${item.id}`),
  userId:
    item?.user?.id !== undefined && item?.user?.id !== null
      ? Number(item.user.id)
      : null,
})

const listRowContextFromKanbanState = (
  state: KanbanState,
  data: KanbanCardSyncDataDto,
  extra?: Pick<KanbanFunnelListRowContext, 'fields' | 'leadState'>,
): KanbanFunnelListRowContext => {
  const column = state.columnsByStatusId[data.status_id]
  const funnel = state.funnels.find((f) => f.id === data.funnel_id)
  return {
    funnelName: funnel?.name ?? '',
    statusName: column?.statusName ?? '',
    statusColor: column?.statusColor ?? '#94a3b8',
    ...extra,
  }
}

const mapKanbanCardFromSyncDto = (
  data: KanbanCardSyncDataDto,
  listRowCtx?: KanbanFunnelListRowContext,
): KanbanCard => ({
  leadFunnelId: data.lead_funnel_id,
  leadId: data.lead_id,
  funnelId: data.funnel_id,
  statusId: data.status_id,
  managerId:
    data.manager?.id !== undefined && data.manager?.id !== null
      ? Number(data.manager.id)
      : null,
  source: null,
  name: data.name,
  phone: data.phone ?? null,
  nextContactAt: data.next_contact_at,
  lastActivityAt: data.last_activity_at,
  updatedAt: data.updated_at,
  sorting: 0,
  vk: data.vk,
  integrations: data.integrations,
  funnelListRow: listRowCtx
    ? buildFunnelListRowFromKanbanSync(data, listRowCtx)
    : undefined,
})

export type HydrateKanbanCardAfterLeadCreateArg =
  | number
  | { leadFunnelId: number; fields?: LeadField[] }

const dispatchUpsertManagerFromGetCard = (
  dispatch: { (action: unknown): void },
  data: KanbanCardSyncDataDto,
) => {
  if (data.manager?.id == null) return
  const mid = Number(data.manager.id)
  if (!Number.isFinite(mid) || mid <= 0) return
  dispatch(
    upsertKanbanManagerFromCard({
      id: mid,
      displayName: String(data.manager.name ?? '').trim(),
    }),
  )
}

const mapCrmKanbanSource = (item: CrmKanbanSourceItem): KanbanSourceOption | null => {
  const id = Number(item.id)
  if (!Number.isFinite(id)) return null
  const label = String(item.name ?? '').trim() || `ID ${id}`
  const color = typeof item.color === 'string' ? item.color : undefined
  return { value: String(id), label, color }
}

export const fetchManagersThunk = createAsyncThunk(
  'crmKanban/fetchManagers',
  async (_, { rejectWithValue }) => {
    try {
      const res = await crmApi.getAllManagers()
      if (isError(res)) throw new Error()
      const items = res.data?.data ?? []
      return items.map(mapManager)
    } catch (error: unknown) {
      return rejectWithValue(
        getErrorMessage(error, 'Не удалось загрузить менеджеров'),
      )
    }
  },
)

export const fetchSourcesThunk = createAsyncThunk(
  'crmKanban/fetchSources',
  async (_, { rejectWithValue }) => {
    try {
      const res = await crmApi.kanban.getSources()
      if (isError(res)) throw new Error()
      const items = res.data?.data?.sources ?? []
      const mapped = items
        .map(mapCrmKanbanSource)
        .filter(Boolean) as KanbanSourceOption[]
      const seen = new Set<string>()
      return mapped.filter((opt) => {
        if (seen.has(opt.value)) return false
        seen.add(opt.value)
        return true
      })
    } catch (error: unknown) {
      return rejectWithValue(
        getErrorMessage(error, 'Не удалось загрузить источники'),
      )
    }
  },
)

export const fetchFunnelsThunk = createAsyncThunk(
  'crmKanban/fetchFunnels',
  async (_, { rejectWithValue }) => {
    try {
      const res = await kanbanApi.getAllFunnels()
      return res
    } catch (error: unknown) {
      return rejectWithValue(
        getErrorMessage(error, 'Не удалось загрузить воронки'),
      )
    }
  },
)

export const fetchFunnelUnassignedCountsThunk = createAsyncThunk(
  'crmKanban/fetchFunnelUnassignedCounts',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as AppState
    const { activeFunnelId, filters, columnSortByStatusId } = state.crmKanban
    if (!activeFunnelId) return rejectWithValue('Funnel is not selected')

    try {
      return await kanbanApi.getFunnelUnassignedCounts({
        funnelId: activeFunnelId,
        filters,
        columnSortByStatusId,
      })
    } catch (error: unknown) {
      return rejectWithValue(
        getErrorMessage(error, 'Не удалось обновить счётчики'),
      )
    }
  },
)

export const fetchBoardInitThunk = createAsyncThunk(
  'crmKanban/fetchBoardInit',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as AppState
    const { activeFunnelId, filters, columnSortByStatusId } = state.crmKanban
    if (!activeFunnelId) return rejectWithValue('Funnel is not selected')

    try {
      const res = await kanbanApi.getBoardInit({
        funnelId: activeFunnelId,
        filters,
        columnSortByStatusId,
        columnsLimit: KANBAN_DEFAULT_COLUMN_ITEMS_LIMIT,
      })
      return res
    } catch (error: unknown) {
      return rejectWithValue(
        getErrorMessage(error, 'Не удалось загрузить канбан'),
      )
    }
  },
)

export type RefetchKanbanBoardArg = { silent?: boolean } | void

export const refetchKanbanBoardThunk = createAsyncThunk(
  'crmKanban/refetchBoard',
  async (arg: RefetchKanbanBoardArg, { getState, rejectWithValue }) => {
    const silent = arg?.silent ?? true
    const state = getState() as AppState
    const { activeFunnelId, filters, columnSortByStatusId, columnsByStatusId } =
      state.crmKanban
    if (!activeFunnelId) {
      return rejectWithValue('Воронка не выбрана')
    }
    if (Object.keys(columnsByStatusId).length === 0) {
      return rejectWithValue('Доска не загружена')
    }

    const statusLimits = buildKanbanStatusLimitsFromColumns(columnsByStatusId)

    try {
      const board = await kanbanApi.getBoardInit({
        funnelId: activeFunnelId,
        filters,
        columnSortByStatusId,
        columnsLimit: KANBAN_DEFAULT_COLUMN_ITEMS_LIMIT,
        statusLimits,
      })
      return { board, silent }
    } catch (error: unknown) {
      return rejectWithValue(
        getErrorMessage(error, 'Не удалось обновить канбан'),
      )
    }
  },
)

export const moveCardThunk = createAsyncThunk(
  'crmKanban/moveCard',
  async (payload: KanbanMoveCardRequest, { rejectWithValue }) => {
    try {
      const server = await kanbanApi.moveCard(payload)
      return { ...payload, server }
    } catch (error: unknown) {
      return rejectWithValue(
        getErrorMessage(error, 'Не удалось переместить карточку'),
      )
    }
  },
)

export const fetchColumnMoreThunk = createAsyncThunk(
  'crmKanban/fetchColumnMore',
  async (statusId: number, { getState, rejectWithValue }) => {
    const state = getState() as AppState
    const {
      activeFunnelId,
      filters,
      columnsByStatusId,
      columnSortByStatusId,
    } = state.crmKanban
    const column = columnsByStatusId[statusId]
    if (!activeFunnelId || !column?.hasMore) return null

    try {
      const res = await kanbanApi.getMoreColumnItems({
        funnelId: activeFunnelId,
        statusId,
        cursor: column.cursor,
        limit: KANBAN_DEFAULT_COLUMN_ITEMS_LIMIT,
        filters,
        columnSortByStatusId,
      })
      return res
    } catch (error: unknown) {
      return rejectWithValue(
        getErrorMessage(error, 'Не удалось догрузить колонку'),
      )
    }
  },
)

export const refetchKanbanColumnThunk = createAsyncThunk(
  'crmKanban/refetchColumn',
  async (arg: RefetchKanbanColumnArg, { getState, rejectWithValue }) => {
    const { statusId, silent } = parseRefetchKanbanColumnArg(arg)
    const state = getState() as AppState
    const { activeFunnelId, filters, columnSortByStatusId } = state.crmKanban
    if (!activeFunnelId) {
      return rejectWithValue('Воронка не выбрана')
    }
    const column = state.crmKanban.columnsByStatusId[statusId]
    if (!column) return rejectWithValue('Колонка не найдена')

    const loadedCount = column.ids.length
    const limit = Math.max(loadedCount, KANBAN_DEFAULT_COLUMN_ITEMS_LIMIT)

    try {
      const res = await kanbanApi.getMoreColumnItems({
        funnelId: activeFunnelId,
        statusId,
        cursor: null,
        limit,
        filters,
        columnSortByStatusId,
      })
      return { ...res, silent }
    } catch (error: unknown) {
      return rejectWithValue(
        getErrorMessage(error, 'Не удалось обновить колонку'),
      )
    }
  },
)

const refetchKanbanColumnIfExists = async (
  dispatch: { (action: unknown): unknown },
  getState: () => unknown,
  statusId: number,
) => {
  const column = (getState() as AppState).crmKanban.columnsByStatusId[statusId]
  if (!column) {
    return
  }
  await dispatch(refetchKanbanColumnThunk({ statusId, silent: true }))
}

/**
 * После модалки лида / закрытия ВК-чата: тихий refetch затронутых колонок
 * (порядок карточек с бэка + column_sort), без патча одной карточки.
 */
export const refreshKanbanColumnsAfterLeadChangeThunk = createAsyncThunk(
  'crmKanban/refreshColumnsAfterLeadChange',
  async (leadFunnelId: number, { getState, dispatch }) => {
    const prevStatusId =
      (getState() as AppState).crmKanban.cardsById[leadFunnelId]?.statusId ??
      null
    const hadCardOnBoard = prevStatusId != null

    try {
      const res = await crmApi.kanban.getCard({ lead_funnel_id: leadFunnelId })
      if (isError(res)) {
        throw new Error()
      }
      const data = (res.data as GetKanbanCardResponse | undefined)?.data
      if (!data) {
        throw new Error()
      }

      dispatchUpsertManagerFromGetCard(dispatch, data)

      const { activeFunnelId, columnsByStatusId } = (getState() as AppState)
        .crmKanban
      const statusIds = new Set<number>()

      if (prevStatusId != null && columnsByStatusId[prevStatusId]) {
        statusIds.add(prevStatusId)
      }

      if (!activeFunnelId || data.funnel_id !== activeFunnelId) {
        if (hadCardOnBoard) {
          dispatch(cardRemoved({ leadFunnelId }))
        }
      } else if (columnsByStatusId[data.status_id]) {
        statusIds.add(data.status_id)
      }

      for (const statusId of statusIds) {
        await refetchKanbanColumnIfExists(dispatch, getState, statusId)
      }

      await dispatch(fetchFunnelUnassignedCountsThunk())
    } catch {
      if (prevStatusId != null) {
        await refetchKanbanColumnIfExists(dispatch, getState, prevStatusId)
      }
      try {
        await dispatch(fetchBoardInitThunk())
      } catch {
        /* ignore */
      }
    }
  },
)

/** После POST insert-lead: подтянуть карточку с getCard и вставить в колонку активной воронки */
export const hydrateKanbanCardAfterLeadCreateThunk = createAsyncThunk(
  'crmKanban/hydrateKanbanCardAfterLeadCreate',
  async (arg: HydrateKanbanCardAfterLeadCreateArg, { getState, dispatch }) => {
    const leadFunnelId =
      typeof arg === 'number' ? arg : Number(arg.leadFunnelId)
    const fieldsFromInsert =
      typeof arg === 'number' ? undefined : arg.fields

    if (!Number.isFinite(leadFunnelId) || leadFunnelId <= 0) return

    const state0 = getState() as AppState
    const existing = state0.crmKanban.cardsById[leadFunnelId]
    if (existing) {
      if (!existing.funnelListRow) {
        try {
          const res = await crmApi.kanban.getCard({
            lead_funnel_id: leadFunnelId,
          })
          if (!isError(res)) {
            const data = (res.data as GetKanbanCardResponse | undefined)?.data
            if (data) {
              const listRowCtx = listRowContextFromKanbanState(
                state0.crmKanban,
                data,
                { fields: fieldsFromInsert },
              )
              dispatch(
                patchKanbanCardFunnelListRow({
                  leadFunnelId,
                  funnelListRow: buildFunnelListRowFromKanbanSync(
                    data,
                    listRowCtx,
                  ),
                }),
              )
            }
          }
        } catch {
          /* sync ниже подтянет карточку */
        }
      }
      await dispatch(refreshKanbanColumnsAfterLeadChangeThunk(leadFunnelId))
      return
    }

    const state = getState() as AppState
    if (!state.crmKanban.activeFunnelId) return

    try {
      const res = await crmApi.kanban.getCard({ lead_funnel_id: leadFunnelId })
      if (isError(res)) throw new Error()
      const data = (res.data as GetKanbanCardResponse | undefined)?.data
      if (!data) throw new Error()

      if (data.funnel_id !== state.crmKanban.activeFunnelId) return

      dispatchUpsertManagerFromGetCard(dispatch, data)
      const listRowCtx = listRowContextFromKanbanState(state.crmKanban, data, {
        fields: fieldsFromInsert,
      })
      dispatch(
        prependKanbanCardFromSync(mapKanbanCardFromSyncDto(data, listRowCtx)),
      )
    } catch {
      await dispatch(fetchBoardInitThunk())
    }
  },
)
